import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { Dumbbell, Plus, Activity, Trash2, X, PlayCircle } from "lucide-react";
import { ExerciseCoachCard } from "@/components/ui/ExerciseDemo";
import { demoFor } from "@/lib/exerciseDemos";

const CATEGORIES = ["all", "Push", "Pull", "Legs", "Core", "Conditioning"];

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

export default function Workouts() {
  const { user } = useAuth();
  const isTrainer = user?.role === "trainer" || user?.role === "admin";
  const qc = useQueryClient();
  const [cat, setCat] = useState("all");
  const [demo, setDemo] = useState(null);

  const { data: workouts = [] } = useQuery({ queryKey: ["workouts"], queryFn: async () => (await api.get("/api/workouts")).data });
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises", cat], queryFn: async () => (await api.get("/api/exercises", { params: cat !== "all" ? { category: cat } : {} })).data });

  const plans = isTrainer ? workouts.filter((w) => !w.assigned_to) : workouts;

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.strength} overline="body · training" title="Workouts"
        subtitle="Trainer-built programmes, week by week and day by day. Every logged set feeds OCTON's brain."
        actions={isTrainer ? <CreatePlan onDone={() => qc.invalidateQueries({ queryKey: ["workouts"] })} /> : null} />

      <Tabs defaultValue="plans">
        <TabsList className="bg-[#0F1115] border border-[#1E222A]">
          <TabsTrigger value="plans" data-testid="tab-plans">Plans</TabsTrigger>
          <TabsTrigger value="library" data-testid="tab-library">Exercise Library</TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {plans.map((w, i) => (
              <motion.div key={w.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="surface rounded-lg p-5 flex flex-col" data-testid={`plan-card-${i}`}>
                <div className="flex items-start justify-between mb-3">
                  <Dumbbell className="w-5 h-5 text-acid" />
                  <Badge variant="outline" className="border-[#343A4A] text-[#8A91A6] mono-label">{w.focus}</Badge>
                </div>
                <h3 className="font-heading font-bold text-lg tracking-tight">{w.name}</h3>
                <p className="text-[#8A91A6] text-sm mt-1 flex-1">{w.description}</p>
                <div className="mt-3 space-y-3">
                  {(w.days || []).map((day, di) => (
                    <div key={day.name || di}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="mono-label text-acid">Day {di + 1}</span>
                        <span className="text-xs text-white/70 font-medium">{day.name}</span>
                        <span className="mono-label ml-auto">{(day.exercises || []).length} moves</span>
                      </div>
                      {(day.exercises || []).slice(0, 5).map((e, j) => (
                        <div key={`${e.exercise}-${j}`} className="flex justify-between text-xs border-b border-[#1E222A] py-1">
                          <span className="text-white/80 inline-flex items-center gap-1">{e.exercise}{demoFor(e.exercise) && <button onClick={() => setDemo({ title: e.exercise, demo: demoFor(e.exercise) })} title="Watch technique" className="text-acid"><PlayCircle className="w-3.5 h-3.5" /></button>}</span>
                          <span className="font-mono2 text-[#8A91A6]">{e.sets}×{e.reps}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                  {(!w.days || w.days.length === 0) && <p className="text-xs text-[#565C6F]">No days programmed yet.</p>}
                </div>
                {!isTrainer && <LogDialog workout={w} onDone={() => qc.invalidateQueries()} />}
              </motion.div>
            ))}
            {plans.length === 0 && <p className="text-[#565C6F] text-sm">No plans yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="library" className="mt-5 space-y-4">
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c}`}
                className={`px-4 h-9 rounded-full text-sm capitalize border transition-colors ${
                  cat === c ? "border-acid text-acid bg-acid/10" : "border-[#1E222A] text-[#8A91A6] hover:border-[#343A4A]"
                }`}>{c}</button>
            ))}
          </div>
          <div className="surface rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1E222A] hover:bg-transparent">
                  <TableHead className="mono-label">Exercise</TableHead>
                  <TableHead className="mono-label">Category</TableHead>
                  <TableHead className="mono-label hidden md:table-cell">Muscle</TableHead>
                  <TableHead className="mono-label hidden md:table-cell">Equipment</TableHead>
                  <TableHead className="mono-label">Level</TableHead>
                  <TableHead className="mono-label">Technique</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exercises.map((e) => (
                  <TableRow key={e.id} className="border-[#1E222A] hover:bg-acid/5" data-testid={`ex-row-${e.name}`}>
                    <TableCell className="font-medium">{e.name}</TableCell>
                    <TableCell className="text-[#8A91A6]">{e.category}</TableCell>
                    <TableCell className="text-[#8A91A6] hidden md:table-cell">{e.muscle_group}</TableCell>
                    <TableCell className="text-[#8A91A6] hidden md:table-cell">{e.equipment}</TableCell>
                    <TableCell><span className="font-mono2 text-xs text-bayes">{e.difficulty}</span></TableCell>
                    <TableCell>{demoFor(e.name) ? (
                      <button onClick={() => setDemo({ title: e.name, demo: demoFor(e.name) })} className="inline-flex items-center gap-1 text-acid text-sm hover:underline"><PlayCircle className="w-4 h-4" /> Watch</button>
                    ) : <span className="text-[#565C6F] text-xs">soon</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
      <Dialog open={!!demo} onOpenChange={(o) => !o && setDemo(null)}>
        <DialogContent className="surface border-[#1E222A] max-w-3xl">
          <DialogHeader><DialogTitle className="font-heading">{demo?.title}{demo && demo.demo !== demo.title ? <span className="text-[#8A91A6] font-normal text-sm"> · shown with {demo.demo}</span> : null}</DialogTitle></DialogHeader>
          {demo && <ExerciseCoachCard name={demo.demo} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LogDialog({ workout, onDone }) {
  const [open, setOpen] = useState(false);
  const [felt, setFelt] = useState(3);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const planExercises = workout.days?.[0]?.exercises || [];

  const buildInitial = () =>
    planExercises.map((e) => {
      const n = Math.max(1, Math.min(6, parseInt(e.sets, 10) || 3));
      return { exercise: e.exercise, target: `${e.sets || ""}${e.reps ? "x" + e.reps : ""}`,
        sets: Array.from({ length: n }, () => ({ reps: e.reps || "", weight: e.weight || "", rpe: "" })) };
    });
  const [rows, setRows] = useState(buildInitial());

  const openChange = (o) => { setOpen(o); if (o) setRows(buildInitial()); };
  const setCell = (ei, si, key, val) =>
    setRows((r) => r.map((row, i) => i !== ei ? row :
      { ...row, sets: row.sets.map((s, j) => j !== si ? s : { ...s, [key]: val }) }));

  const submit = async () => {
    setBusy(true);
    try {
      const entries = rows.map((r) => ({ exercise: r.exercise, target: r.target, sets: r.sets }));
      const { data } = await api.post(`/api/workouts/${workout.id}/log`, {
        workout_name: workout.name, day_name: workout.days?.[0]?.name || "", entries, felt: Number(felt), notes,
      });
      const enc = data.brain?.encoded?.length || 0;
      toast.success(`Session logged · OCTON encoded ${enc} concept${enc === 1 ? "" : "s"}`);
      setOpen(false); setNotes(""); setFelt(3);
      onDone?.();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={openChange}>
      <DialogTrigger asChild>
        <Button className="mt-4 w-full bg-white text-black hover:bg-acid rounded-md h-10 font-medium transition-colors" data-testid={`log-btn-${workout.name}`}>
          <Activity className="w-4 h-4" /> Log session
        </Button>
      </DialogTrigger>
      <DialogContent className="surface border-[#1E222A] max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Log · {workout.name}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          {rows.length > 0 && (
            <div className="space-y-4">
              {rows.map((r, ei) => (
                <div key={`${r.exercise}-${ei}`} className="surface-elev rounded-md p-3" data-testid={`log-ex-${ei}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">{r.exercise}</span>
                    <span className="mono-label">{r.target}</span>
                  </div>
                  <div className="grid grid-cols-[auto_1fr_1fr_1fr] gap-2 items-center">
                    <span className="mono-label">SET</span>
                    <span className="mono-label">REPS</span>
                    <span className="mono-label">WEIGHT</span>
                    <span className="mono-label">RPE</span>
                    {r.sets.map((s, si) => (
                      <ScopeRow key={`${r.exercise}-${si}`} idx={si + 1}
                        reps={s.reps} weight={s.weight} rpe={s.rpe}
                        onReps={(v) => setCell(ei, si, "reps", v)}
                        onWeight={(v) => setCell(ei, si, "weight", v)}
                        onRpe={(v) => setCell(ei, si, "rpe", v)}
                        testid={`set-${ei}-${si}`} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div>
            <Label className="mono-label">How did it feel? ({felt}/5)</Label>
            <div className="grid grid-cols-5 gap-2 mt-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setFelt(n)} data-testid={`felt-${n}`}
                  className={`h-11 rounded-md border font-mono2 transition-colors ${
                    felt === n ? "border-acid text-acid bg-acid/10" : "border-[#1E222A] text-[#8A91A6] hover:border-[#343A4A]"
                  }`}>{n}</button>
              ))}
            </div>
            <p className="text-xs text-[#565C6F] mt-1.5">1 = very hard · 5 = unstoppable. Drives OCTON's prediction-error pulse.</p>
          </div>
          <div>
            <Label className="mono-label">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" placeholder="Optional…" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="submit-log-btn" className="bg-acid text-black hover:bg-[#AACC00]">
            {busy ? "Encoding…" : "Log & feed OCTON"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScopeRow({ idx, reps, weight, rpe, onReps, onWeight, onRpe, testid }) {
  return (
    <>
      <span className="font-mono2 text-xs text-[#8A91A6]">{idx}</span>
      <Input value={reps} onChange={(e) => onReps(e.target.value)} className="h-9 bg-[#0F1115] border-[#1E222A]" data-testid={`${testid}-reps`} placeholder="-" />
      <Input value={weight} onChange={(e) => onWeight(e.target.value)} className="h-9 bg-[#0F1115] border-[#1E222A]" placeholder="kg" />
      <Input value={rpe} onChange={(e) => onRpe(e.target.value)} className="h-9 bg-[#0F1115] border-[#1E222A]" placeholder="1-10" />
    </>
  );
}

function CreatePlan({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", focus: "Full Body", description: "" });
  const [days, setDays] = useState([{ _k: uid(), name: "Day 1", exercises: [] }]);
  const [busy, setBusy] = useState(false);
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises", "all"], queryFn: async () => (await api.get("/api/exercises")).data, enabled: open });

  const addDay = () => setDays((d) => [...d, { _k: uid(), name: `Day ${d.length + 1}`, exercises: [] }]);
  const removeDay = (di) => setDays((d) => d.filter((_, i) => i !== di));
  const setDayName = (di, v) => setDays((d) => d.map((day, i) => i === di ? { ...day, name: v } : day));
  const addExercise = (di, name) => setDays((d) => d.map((day, i) => i === di
    ? { ...day, exercises: [...day.exercises, { _k: uid(), exercise: name, sets: 3, reps: "8", weight: "" }] } : day));
  const setEx = (di, ei, key, v) => setDays((d) => d.map((day, i) => i !== di ? day
    : { ...day, exercises: day.exercises.map((e, j) => j === ei ? { ...e, [key]: v } : e) }));
  const removeEx = (di, ei) => setDays((d) => d.map((day, i) => i !== di ? day
    : { ...day, exercises: day.exercises.filter((_, j) => j !== ei) }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = days.map(({ _k, exercises, ...d }) => ({ ...d, exercises: exercises.map(({ _k: _ek, ...e }) => e) }));
      await api.post("/api/workouts", { ...form, days: payload });
      toast.success("Plan created");
      setOpen(false); setForm({ name: "", focus: "Full Body", description: "" }); setDays([{ _k: uid(), name: "Day 1", exercises: [] }]);
      onDone?.();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-acid text-black hover:bg-[#AACC00] rounded-full" data-testid="new-plan-btn"><Plus className="w-4 h-4" /> New plan</Button>
      </DialogTrigger>
      <DialogContent className="surface border-[#1E222A] max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Plan builder</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mono-label">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" data-testid="plan-name" /></div>
            <div><Label className="mono-label">Focus</Label><Input value={form.focus} onChange={(e) => setForm({ ...form, focus: e.target.value })} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" /></div>
          </div>
          <div><Label className="mono-label">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" /></div>

          {days.map((day, di) => (
            <div key={day._k} className="surface-elev rounded-md p-4" data-testid={`day-${di}`}>
              <div className="flex items-center gap-2 mb-3">
                <Input value={day.name} onChange={(e) => setDayName(di, e.target.value)} className="h-9 bg-[#0F1115] border-[#1E222A] font-medium max-w-[200px]" />
                {days.length > 1 && (
                  <button onClick={() => removeDay(di)} className="text-[#565C6F] hover:text-pulseError transition-colors" data-testid={`remove-day-${di}`}><X className="w-4 h-4" /></button>
                )}
              </div>
              <div className="space-y-2">
                {day.exercises.map((e, ei) => (
                  <div key={e._k} className="grid grid-cols-[1fr_64px_64px_72px_auto] gap-2 items-center" data-testid={`day-${di}-ex-${ei}`}>
                    <span className="text-sm truncate">{e.exercise}</span>
                    <Input value={e.sets} onChange={(v) => setEx(di, ei, "sets", v.target.value)} className="h-9 bg-[#0F1115] border-[#1E222A]" placeholder="sets" />
                    <Input value={e.reps} onChange={(v) => setEx(di, ei, "reps", v.target.value)} className="h-9 bg-[#0F1115] border-[#1E222A]" placeholder="reps" />
                    <Input value={e.weight} onChange={(v) => setEx(di, ei, "weight", v.target.value)} className="h-9 bg-[#0F1115] border-[#1E222A]" placeholder="wt" />
                    <button onClick={() => removeEx(di, ei)} className="text-[#565C6F] hover:text-pulseError transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <Select onValueChange={(v) => addExercise(di, v)}>
                <SelectTrigger className="mt-3 h-9 bg-[#0F1115] border-[#1E222A]" data-testid={`add-ex-${di}`}><SelectValue placeholder="+ Add exercise…" /></SelectTrigger>
                <SelectContent className="surface border-[#1E222A] max-h-64">
                  {exercises.map((ex) => <SelectItem key={ex.id} value={ex.name}>{ex.name} · {ex.category}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
          <Button onClick={addDay} variant="outline" className="w-full border-dashed border-[#343A4A] bg-transparent hover:bg-white/5 h-10" data-testid="add-day-btn">
            <Plus className="w-4 h-4" /> Add day
          </Button>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy || !form.name} className="bg-acid text-black hover:bg-[#AACC00]" data-testid="submit-plan-btn">Create plan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
